import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { mergeMap, Observable, Observer, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import * as Sentry from '@sentry/angular';

@Injectable({
  providedIn: 'root'
})
export class AudioPlayerService {
  private readonly http = inject(HttpClient);

  private currentlyPlaying?: {
    element: HTMLAudioElement,
    observer: Observer<MediaError | undefined>,
    objectUrl: string,
  };

  play = (audioFile: string, playbackSpeed: number = 1): Observable<MediaError | undefined> => {
    // Separately fetch the audio blob instead of new Audio("url") to help with debugging iOS audio playback errors (failed to decode)
    // Hopefully temporary
    // sometimes cloudfront returns a 307 OK redirect that angular needs to follow
    const file$ = this.http.get(`${environment.AssetServerURL}/assets/` + audioFile, { responseType: 'blob', redirect: 'follow' });

    return file$.pipe(
      mergeMap(f => {
        const objectUrl = URL.createObjectURL(f);
        const audioElement = new Audio(objectUrl);

        audioElement.playbackRate = playbackSpeed;

        let audioObservable = new Observable<MediaError | undefined>((observer) => {
          this.stopAll();
          const playing = { element: audioElement, observer, objectUrl };
          this.currentlyPlaying = playing;

          let completeHandler = () => {
            observer.next(undefined);
            observer.complete();
          }
          let errorHandler = () => {
            const error = audioElement.error;
            console.error('AudioPlayerService.play error playing ', audioFile, ' is of length ', f?.size ?? 0, ':', error ? (`${error.code}: ${error.message}`) : "");
            observer.error(audioElement.error);
          }

          audioElement.addEventListener('ended', completeHandler);
          audioElement.addEventListener('error', errorHandler);

          audioElement.play().catch((error) => {
            if (
              (error as DOMException)?.name === "AbortError"
              ||  (error as DOMException)?.name === "NotAllowedError"
            ) {
              // AbortError can be due to pause or unsubscribe
              // NotAllowedError can be caused if the user doesn't interact with the page before audio plays
              // either way we don't want to escalate these to actual errors, as they happen routinely and don't have too detrimental of effects
              completeHandler();
            } else {
              observer.error(error);
            }
          });

          return {
            unsubscribe: () => {
              audioElement.removeEventListener('ended', completeHandler);
              audioElement.removeEventListener('error', errorHandler);
              this.stopAll();
            }
          }
        });

        return audioObservable;
      })
    );
  }

  stopAll = () => {
    if (this.currentlyPlaying) {
      this.currentlyPlaying.element.pause();
      this.currentlyPlaying.element.currentTime = 0;
      this.currentlyPlaying.element.src = ''; // clear url to pervent browser from trying to access url we are about to invalidate
      URL.revokeObjectURL(this.currentlyPlaying.objectUrl);
      this.currentlyPlaying.observer.next(undefined);
      this.currentlyPlaying.observer.complete();
      this.currentlyPlaying = undefined;
    }
  };
 }
